import { Alert, Col, Divider, Row } from 'antd';
import PrintForm2 from 'mixins/ui/print-form-2';
import { PDFDocumentProxy } from 'pdfjs-dist';
import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { localize } from '/react4xp/mixins/helpers';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './columns.scss';
import Button from '/react4xp/mixins/ui/button/button';
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const columns = (
    translations: Record<string, any>,
    token: any,
    printUrl: string,
    data: any
) => [
    {
        title: localize('fno_nz007-submission-date', translations),
        dataIndex: ['nzData', 'submissionDate'],
    },
    {
        title: localize('base', translations),
        dataIndex: ['nzData', 'documentType'],
        render: (documentType) =>
            documentType === 'DECISION'
                ? localize('fno_nz007-decision-tax-authority', translations)
                : localize('fno_nz007-tax-statement-withdrawal', translations),
    },
    {
        title: localize('result', translations),
        dataIndex: ['nzData', 'status'],
        render: (status) =>
            status === 'processed'
                ? localize('fno_nz007-processed', translations)
                : localize('fno_nz007-processed-with-rejection', translations),
    },
    {
        title: localize('fno_nz007-detailed-information', translations),
        render: (_, row) => {
            return (
                <DetailedInfoCol
                    row={row}
                    token={token}
                    printUrl={printUrl}
                    translations={translations}
                    data={data}
                />
            );
        },
    },
];

type PrintFormRowProps = {
    keyName: string;
    value: string;
};

const PrintFormRow = ({ keyName, value }: PrintFormRowProps) => (
    <>
        <Col
            span={9}
            offset={2}
        >
            {keyName}
        </Col>
        <Col span={11}>{value}</Col>
    </>
);

const DetailedInfoCol = ({ row, token, printUrl, translations, data }) => {
    const [numPages, setNumPages] = useState<number>();

    function onDocumentLoadSuccess({ numPages: nextNumPages }: PDFDocumentProxy): void {
        setNumPages(nextNumPages);
    }

    return (
        <PrintForm2
            tableModal
            tokenStr={token}
            params={{ regNumber: row.nzData.regNumber }}
            apiUrl={printUrl}
            translations={translations}
            renderFooter={() => null}
            renderContent={(pdfData: any, print, closeModal) => {
                const blob = new Blob([pdfData.data], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);

                return (
                    <Row style={{ height: '100%' }}>
                        <Col span={10}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    height: '100%',
                                }}
                            >
                                <Row gutter={[16, 10]}>
                                    <Col
                                        span={20}
                                        offset={2}
                                    >
                                        {row?.nzData?.status === 'processed' ? (
                                            <Alert
                                                style={{
                                                    width: 120,
                                                    marginTop: 40,
                                                    color: '#47a25f',
                                                    backgroundColor: '#d6eae1',
                                                    textAlign: 'center',
                                                }}
                                                message={localize(
                                                    'fno_nz007-processed',
                                                    translations
                                                )}
                                                type="success"
                                            />
                                        ) : (
                                            <Alert
                                                style={{
                                                    width: 240,
                                                    marginTop: 40,
                                                    color: '#47ba77',
                                                    backgroundColor: '#dff3e6',
                                                    textAlign: 'center',
                                                }}
                                                message={localize(
                                                    'fno_nz007-processed-with-rejection',
                                                    translations
                                                )}
                                                type="error"
                                            />
                                        )}
                                    </Col>

                                    <Col
                                        span={20}
                                        offset={2}
                                    >
                                        <Divider
                                            style={{
                                                margin: '5px 0',
                                            }}
                                        />
                                    </Col>

                                    <PrintFormRow
                                        keyName={localize('fno_nz007-ogd-name', translations)}
                                        value={row?.taxOrg?.ru}
                                    />

                                    <PrintFormRow
                                        keyName={localize('fno_nz007-ogd-code', translations)}
                                        value={row?.taxOrg?.code}
                                    />

                                    <PrintFormRow
                                        keyName={localize(
                                            'fno_nz007-taxpayer-iin-bin',
                                            translations
                                        )}
                                        value={data?.taxpayerData?.taxpayerCode}
                                    />

                                    <PrintFormRow
                                        keyName={localize('fno_nz007-taxpayer-name', translations)}
                                        value={data?.taxpayerData?.taxpayerNameRu}
                                    />

                                    <PrintFormRow
                                        keyName={localize(
                                            'fno_nz007-tax-statement-reg-number',
                                            translations
                                        )}
                                        value={row?.nzData?.regNumber}
                                    />

                                    <PrintFormRow
                                        keyName={localize(
                                            'fno_nz007-tax-statement-submission-date',
                                            translations
                                        )}
                                        value={row?.nzData?.submissionDate}
                                    />
                                </Row>

                                <Row
                                    justify="start"
                                    gutter={25}
                                    style={{
                                        margin: 0,
                                        padding: '0.5rem 1rem',
                                        paddingBottom: 20,
                                    }}
                                >
                                    <Col
                                        span={20}
                                        offset={2}
                                        style={{
                                            padding: 0,
                                        }}
                                    >
                                        <Divider
                                            style={{
                                                margin: '15px 0',
                                            }}
                                        />
                                    </Col>
                                    <Col
                                        offset={2}
                                        style={{ padding: 0 }}
                                    >
                                        <Button
                                            type="primary"
                                            color="gold"
                                            onClick={print}
                                        >
                                            {localize('button.print', translations)}
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button onClick={closeModal}>
                                            {localize('button.close', translations)}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                        <Col
                            span={14}
                            style={{
                                backgroundColor: '#f4f8fb',
                            }}
                        >
                            <div
                                style={{
                                    overflowY: 'scroll',
                                    height: '100vh',
                                }}
                            >
                                <div className="Example__container__document">
                                    <Document
                                        file={url}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                    >
                                        {Array.from(new Array(numPages), (_el, index) => (
                                            <Page
                                                key={`page_${index + 1}`}
                                                pageNumber={index + 1}
                                            />
                                        ))}
                                    </Document>
                                </div>
                            </div>
                        </Col>
                    </Row>
                );
            }}
        />
    );
};
